import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';

import CloseIcon from '@mui/icons-material/CloseRounded';
import CheckIcon from '@mui/icons-material/CheckRounded';
import MovieIcon from '@mui/icons-material/MovieCreationRounded';
import BoltIcon from '@mui/icons-material/BoltRounded';

import api from '../../services/api';

export default function VideoCreditsModal({ open, onClose, reason }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const isLimitExceeded = reason === 'need_video_credits' || reason === 'pro_max_limit_exceeded';

    const PERKS = [
        t('videoCredits.perk1'),
        t('videoCredits.perk2'),
        t('videoCredits.perk3'),
        t('videoCredits.perk4'),
    ];

    const handleBuyCredits = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await api.post('/billing/stripe/video-credit-topup/', { amount_euros: 15 });
            if (res.data?.url) {
                window.location.href = res.data.url;
            } else if (res.data?.dev_mode) {
                onClose && onClose(true);
            } else {
                setError(res.data?.error || t('videoCredits.error'));
            }
        } catch (e) {
            setError(e?.response?.data?.error || t('videoCredits.error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    background: '#fff',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.14)',
                    overflow: 'hidden',
                }
            }}
        >
            <DialogContent sx={{ p: 0 }}>
                {/* Header gradient */}
                <Box sx={{
                    background: 'linear-gradient(135deg, #18181B 0%, #27272A 100%)',
                    px: 3, pt: 3, pb: 2.5,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <MovieIcon sx={{ color: '#A855F7', fontSize: '1.2rem' }} />
                            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#fff' }}>
                                {t('videoCredits.title')}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)' }}>
                            {t('videoCredits.subtitle')}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{ color: 'rgba(255,255,255,0.4)', mt: -0.5, '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.1)' } }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Body */}
                <Box sx={{ p: 3 }}>
                    {/* Cost badge */}
                    <Box sx={{
                        background: 'linear-gradient(135deg, rgba(168,85,247,0.08), rgba(112,18,128,0.06))',
                        border: '1px solid rgba(168,85,247,0.2)',
                        borderRadius: '14px',
                        p: 2,
                        mb: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                    }}>
                        <Box sx={{
                            width: 48, height: 48, borderRadius: '12px',
                            background: 'linear-gradient(135deg, #701280, #A855F7)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                            <BoltIcon sx={{ color: '#fff', fontSize: '1.4rem' }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#18181B', lineHeight: 1 }}>
                                ~€5 {t('videoCredits.perVideo')}
                            </Typography>
                            <Typography sx={{ fontSize: '0.78rem', color: '#6B6B7B', mt: 0.3 }}>
                                {t('videoCredits.minTopup')}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Perks */}
                    {PERKS.map((p, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 0.85 }}>
                            <CheckIcon sx={{ fontSize: '0.9rem', color: '#701280', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.83rem', color: '#5B5B6B' }}>{p}</Typography>
                        </Box>
                    ))}

                    {error && (
                        <Typography sx={{ mt: 1.5, color: '#EF4444', fontSize: '0.82rem', textAlign: 'center' }}>
                            {error}
                        </Typography>
                    )}

                    {/* CTA */}
                    <Button
                        variant="contained"
                        fullWidth
                        disabled={loading}
                        onClick={handleBuyCredits}
                        sx={{
                            mt: 2.5,
                            background: 'linear-gradient(135deg, #701280, #A855F7)',
                            borderRadius: '11px',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            py: 1.3,
                            boxShadow: 'none',
                            '&:hover': { background: 'linear-gradient(135deg, #5A0F68, #8B3FCF)', boxShadow: '0 4px 16px rgba(112,18,128,0.35)' },
                            '&:disabled': { background: 'rgba(0,0,0,0.09)', boxShadow: 'none' },
                        }}
                    >
                        {loading
                            ? '...'
                            : t('videoCredits.cta')}
                    </Button>

                    <Typography sx={{ fontSize: '0.7rem', color: '#BABAC8', textAlign: 'center', mt: 1.2 }}>
                        {t('videoCredits.autoRecharge')}
                    </Typography>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
